import React from 'react';
import {connect} from 'react-redux';
import FormBody from "./../form/FormBody";
import FormActions from "./../form/FormActions";
import i18n from "./../i18n/i18n";

/**
 * Akce pro modální dialog.
 * @type {string}
 */

export const SHOW = 'ModalDialog.show';
export const HIDE = 'ModalDialog.hide';
export const CHANGE_SIZE = 'ModalDialog.changeSize';
export const CHANGE_TITLE = 'ModalDialog.changeTitle';

/**
 * Zobrazení modálního dialogu. Pokud je nějaký dialog zobrazen, zobrazáí se nad ním další.
 * @param component reference na komponentu
 * @param componentProps props, které budou předávány při renderování komponenty
 * @param title titulek dialogu
 * @param modalProps doplňující props pro dialog, všechny kromě níže uvedených se předají na <Modal/>
 *                  podporované props:
 *                      noBackdrop pokud je true, nezobrazuje se pod tímto dialogem černé pozadí, ale jen průhledné, hodí se např. na donačítací async loading dialogy
 *                      className (doplňující třída)
 *                      onClose (on close callback)
 *                      size (md, lg, xl nebo full (skoro přes celou obrazovku))
 *                      noHeader (true, pokud nechceme header dialogu
 *
 * @returns {{type: string, component: *, componentProps: *, title: *}}
 */
export function show(component, componentProps, title, modalProps = {}) {
    return {
        type: SHOW,
        component,
        componentProps,
        title,
        modalProps,
    }
}

/**
 * Funkce pro změnu velikosti aktuálního dialogu.
 * @param size nová velikost dialog, může být null pro použití výchozí, se kterou bylo voláno zobrazení dialogu, velikosti viz metoda show
 */
export function changeSize(size) {
    return {
        type: CHANGE_SIZE,
        size,
    }
}

/**
 * Funkce pro změnu titulku aktuálního dialogu.
 * @param title nový titulek
 */
export function changeTitle(title) {
    return {
        type: CHANGE_TITLE,
        title,
    }
}

const getRenderableElement = (element) => {
    if (typeof element === "string" || React.isValidElement(element)) {
        return element;
    } else {
        return React.createElement(element);
    }
};

/**
 * Informativní dialog s titulkem a textem
 * @param message
 * @param dispatch
 * @param onClose on close callback
 * @param cards jedná se o info dialog s kartičkami, implicitně false
 */
const infoDialog = (message, dispatch, onClose, cards = false) => {
    const msg = getRenderableElement(message);

    return () => (
        <div>
            <FormBody cards={cards}>
                {msg}
            </FormBody>
            <FormActions closeButton onClose={() => { dispatch(hide()); onClose && onClose() }} />
        </div>
    )
};

const confirmDialog = (message, dispatch, onSubmit, confirmButtonText = null, cancelButtonText = null) => {
    const handleSubmit = () => {
        dispatch(hide());
        onSubmit();
    };
    return (props) => {
        const msg = getRenderableElement(message);

        return (
            <div>
                <FormBody>
                    {msg}
                </FormBody>
                <FormActions onSubmit={handleSubmit} submitButton={confirmButtonText ? confirmButtonText : i18n("global.form.action.yes")} cancelButton={cancelButtonText ? cancelButtonText : true} onClose={props.onClose} />
            </div>
        )
    }
};

const AsyncWaitingDialog = connect()(class extends React.Component {
    constructor(props) {
        super(props);

        const {dispatch, callAsync, resultCallback, errorCallback} = props;

        callAsync
            .then((...data) => {
                dispatch(hide());
                resultCallback && resultCallback(...data);
            })
            .catch((...error) => {
                dispatch(hide());
                console.error(error);
                errorCallback && errorCallback(...error);
            });
    }

    render() {
        const {title, message} = this.props;

        const tit = getRenderableElement(title);
        const msg = getRenderableElement(message);
        return <div>
            <FormBody>
                {tit && <h2>{tit}</h2>}
                {msg}
            </FormBody>
        </div>
    }
});

/**
 * Zobrazení dialogu pro asynchronní operace, např. čekání na data nebo čekání na tisk apod.
 * @param title titulek zprávy pro uživatele, není povinné
 * @param message text zprávy
 * @param callAsync asynchronní operace, která má být zavolána, reference na funkci
 * @param resultCallback callbak po úspěšném zavolání callAsync
 * @param errorCallback callback po chybě po zavolání callback
 * @return {function(*, *)}
 */
export function showAsyncWaiting(title, message, callAsync, resultCallback, errorCallback) {
    return (dispatch, getState) => {
        dispatch(show(AsyncWaitingDialog, {title, message, callAsync, resultCallback, errorCallback}, null, { noHeader: true, noBackdrop: true }));
    }
}

/**
 * Zobrazí informativní dialog s titulkem a textem
 * @param title
 * @param message
 * @param onClose on close callback
 * @param cards jedná se o info dialog s kartičkami, implicitně false
 * @param modalProps viz metoda show
 */
export function showInfo(title, message, onClose, cards = false, modalProps = {}) {
    return (dispatch, getState) => {
        const useModalProps = {
            ...modalProps,
            onClose
        };

        dispatch(show(infoDialog(message, dispatch, onClose, cards), null, title, useModalProps));
    }
}
/**
 * Zobrazí potvrzovací dialog s titulkem a textem
 * @param title
 * @param message
 * @param onSubmit potvrzení dialogu
 * @param onCancel zrušení dialogu
 * @param confirmButtonText pokud je definováno, definuje text potvrzovacího tlačítka
 * @param cancelButtonText pokud je definováno, definuje text uzavíracího tlačítka
 */
export function showConfirm(title, message, onSubmit, onCancel, confirmButtonText = null, cancelButtonText = null) {
    return (dispatch, getState) => {
        dispatch(show(
            confirmDialog(message, dispatch, onSubmit, confirmButtonText, cancelButtonText),
            {onClose: onCancel},
            title
        ));
    }
}

/**
 * Zavření aktuálního dialogu. Pokud je jich otevřeno více, zavře se poslední otevřený.
 * @returns {function(*, *)}
 */
export function hide() {
    return (dispatch, getState) => {
        if (getState().modalDialog.components.length > 0) {
            dispatch({
                type: HIDE,
            })
        }
    }
}
